import { Box, Stack, Typography } from "@mui/material";
import { FC, memo } from "react";
import { Path, useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { Select } from "@/modules/irohana-components/elements/Select";
import { divider, text } from "@/theme/colors";
import {
  REFUND_TENURE_OPTIONS,
  REFUND_TYPE_OPTIONS,
  TENURE_OPERATOR_OPTIONS,
} from "@/features/organization/product/constants/productEnums";
import { TProductForm } from "@/api/services/main/organization/product/dtos/product.dto";
import { usedValuesExcept } from "@/features/organization/product/utils/productUtils";

/** 返金条件設定 — always exactly 5 rows (never add/remove); empty rows submit as nothing. */
export const ProductRefundSection: FC = memo(() => {
  const methods = useFormContext<TProductForm>();
  const { control } = methods;
  const { fields } = useFieldArray({ control, name: "refundConditions" });
  // A 在職期間 value picked in one row is disabled in the others (no duplicate tenure).
  const rows = useWatch({ control, name: "refundConditions" });
  const tenureByRow = (rows ?? []).map((r) => r?.tenureMonths ?? null);

  const name = (i: number, key: string) => `refundConditions.${i}.${key}` as Path<TProductForm>;
  const disabledTenures = (i: number): string[] => usedValuesExcept(tenureByRow, i);

  return (
    <CollapsibleCard title="返金条件設定">
      <Stack sx={{ border: `1px solid ${divider.middle}`, borderRadius: 2, overflow: "hidden" }}>
        <Stack direction="row" sx={{ bgcolor: divider.low, px: 2, py: 1.25 }}>
          <Typography variant="cap12Semi" color={text.secondary} sx={{ flex: 1 }}>在職期間</Typography>
          <Typography variant="cap12Semi" color={text.secondary} sx={{ flex: 1, pl: 4 }}>返金額（返金率）</Typography>
        </Stack>
        {fields.map((field, i) => (
          <Stack
            key={field.id}
            direction="row"
            alignItems="flex-start"
            gap={4}
            sx={{ px: 2, py: 1.25, borderTop: `1px solid ${divider.low}` }}
          >
            <Stack direction="row" gap={1} sx={{ flex: 1 }}>
              <Box sx={{ flex: 1 }}>
                <FormField control={control} name={name(i, "tenureMonths")} noLabel isMessageNowrap>
                  <Select options={REFUND_TENURE_OPTIONS} disabledValues={disabledTenures(i)} />
                </FormField>
              </Box>
              <Box sx={{ width: 100 }}>
                <FormField control={control} name={name(i, "tenureOperator")} noLabel>
                  <Select options={TENURE_OPERATOR_OPTIONS} clearable={false} />
                </FormField>
              </Box>
            </Stack>
            <Stack direction="row" gap={1} sx={{ flex: 1 }}>
              <Box sx={{ flex: 1 }}>
                <FormField control={control} name={name(i, "refundValue")} noLabel isMessageNowrap>
                  <Input placeholder="入力してください" />
                </FormField>
              </Box>
              <Box sx={{ width: 100 }}>
                <FormField control={control} name={name(i, "refundType")} noLabel>
                  <Select options={REFUND_TYPE_OPTIONS} clearable={false} />
                </FormField>
              </Box>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </CollapsibleCard>
  );
});

ProductRefundSection.displayName = "ProductRefundSection";
